import { useInfiniteQuery } from 'react-query';

import { ManualVersion } from 'views/App/Portal/Policy/_types/manuals/draft';
import { ApiCollectionResponse } from 'types/api_response';
import {
  infiniteEmptyResults,
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
} from 'data_sources/constants';

export default function useLegacyArchivedManualVersions<T extends ManualVersion>(
  organizationId: string,
  manualId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexipub/manuals/archived/v1/legacy-manuals/${manualId}`;

  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['legacy_archived_manual_versions', organizationId, manualId, params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
